import { Box, Chip, CircularProgress, TextField } from "@mui/material";
import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { DashboardWrapper } from "src/components/common/DashboardWrapper";
import { Button } from "src/components/common/FormComponents/Buttons";
import { path } from "src/constants/path";
import { dateAndTimeStamp } from "src/utils/helper";
import { fileUpload, getFileByKey } from "src/utils/api/fileupload";
import {
  createPaymentQuery,
  getPaymentByEpid,
  uploadReceipt,
} from "src/utils/api/payment";
import { adminInstance } from "src/dataProvider/axios";
import { useAppSelector } from "src/redux/store";

// Same host the API calls run against (axios instance is the source of truth;
// the env vars may point at prod while local dev hits 127.0.0.1).
const API_BASE_URL =
  adminInstance.defaults.baseURL || "http://127.0.0.1:3000/api/";

const PAYMENT_STATUS_LABELS: Record<string, string> = {
  DEMAND_NOTE_PENDING: "Demand Note Pending",
  PAYMENT_PENDING: "Payment Pending",
  PAYMENT_VERIFICATION: "Payment Verification",
  PAYMENT_REJECTED: "Payment Rejected",
  PAYMENT_RECEIVED: "Payment Received",
};

const PAYMENT_STATUS_COLORS: Record<string, string> = {
  DEMAND_NOTE_PENDING: "#757575",
  PAYMENT_PENDING: "#1976d2",
  PAYMENT_VERIFICATION: "#ed6c02",
  PAYMENT_REJECTED: "#d32f2f",
  PAYMENT_RECEIVED: "#2e7d32",
};

const greenLinkSx = {
  color: "#36B1A0",
  fontWeight: 500,
  cursor: "pointer",
  textDecoration: "none",
  "&:hover": { textDecoration: "underline" },
};

const uploadDocument = async (file: File) => {
  const form = new FormData();
  form.append("file", file);
  const res = await fileUpload(form as any);
  return {
    key: res.data?.data?.uploadedFile?.key,
    name: file.name,
  };
};

const downloadDocByKey = async (docKey: string, fileName?: string) => {
  try {
    const res = await getFileByKey(docKey);
    const url =
      (res as any)?.data?.data?.fileUrl || (res as any)?.data?.fileUrl;
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    if (fileName) a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (err) {
    console.error("Failed to download document", err);
  }
};

const Field = ({
  label,
  value,
  children,
}: {
  label: string;
  value?: any;
  children?: React.ReactNode;
}) => (
  <Box>
    <Box sx={{ fontSize: 12, color: "#666", mb: 0.5 }}>{label}</Box>
    <Box sx={{ fontSize: 14, fontWeight: 500 }}>{children ?? value ?? "—"}</Box>
  </Box>
);

const DocDownloadButton = ({
  docKey,
  docName,
}: {
  docKey?: string;
  docName?: string;
}) => {
  const [loading, setLoading] = useState(false);
  if (!docKey) return <Box sx={{ color: "#999" }}>—</Box>;
  return (
    <Button
      text={loading ? "Opening..." : "Download"}
      onClick={async () => {
        if (loading) return;
        setLoading(true);
        try {
          await downloadDocByKey(docKey, docName);
        } finally {
          setLoading(false);
        }
      }}
    />
  );
};

const QueryThread = ({ queries }: { queries: any[] }) => {
  if (!queries?.length) return null;
  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ fontSize: 16, fontWeight: 600, mb: 1 }}>Queries</Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {queries.map((q) => {
          const isAdmin = q.userType === "ADMIN";
          return (
            <Box
              key={q.id}
              sx={{
                p: 1.5,
                border: "1px solid #eee",
                borderLeft: `3px solid ${isAdmin ? "#1976d2" : "#36B1A0"}`,
                borderRadius: 1,
                background: "#fafafa",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 0.5,
                  flexWrap: "wrap",
                }}
              >
                <Chip
                  label={isAdmin ? "Admin" : "Entity"}
                  size="small"
                  sx={{
                    backgroundColor: isAdmin ? "#1976d220" : "#36B1A020",
                    color: isAdmin ? "#1976d2" : "#36B1A0",
                    fontWeight: 600,
                    fontSize: 11,
                  }}
                />
                <Box sx={{ fontSize: 13, fontWeight: 500 }}>
                  {q.userName ?? "—"}
                </Box>
                <Box sx={{ fontSize: 12, color: "#666", ml: "auto" }}>
                  {dateAndTimeStamp(q.createdAt, "IST")}
                </Box>
              </Box>
              <Box sx={{ fontSize: 14, whiteSpace: "pre-wrap" }}>{q.query}</Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

const RaiseQueryForm = ({
  epid,
  onDone,
}: {
  epid: string;
  onDone: () => void;
}) => {
  const [text, setText] = useState("");
  const [error, setError] = useState("");

  const { mutate, isLoading } = useMutation(createPaymentQuery, {
    onSuccess: () => {
      setText("");
      onDone();
    },
    onError: (err: any) =>
      setError(err?.response?.data?.message || "Failed to submit query"),
  });

  const handleSubmit = () => {
    setError("");
    if (!text.trim()) return setError("Query cannot be empty");
    mutate({ epid, query: text });
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 1.5, maxWidth: 600 }}
    >
      <Box sx={{ fontSize: 16, fontWeight: 600 }}>Raise Query</Box>
      <TextField
        placeholder="Type your query..."
        value={text}
        onChange={(e) => setText(e.target.value)}
        size="small"
      />
      {error && <Box sx={{ color: "#d32f2f", fontSize: 13 }}>{error}</Box>}
      <Box>
        <Button
          text="Submit Query"
          loading={isLoading}
          onClick={handleSubmit}
        />
      </Box>
    </Box>
  );
};

const UploadReceiptForm = ({
  payment,
  onDone,
}: {
  payment: any;
  onDone: () => void;
}) => {
  const [doc, setDoc] = useState<{ key: string; name: string } | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const { mutate, isLoading } = useMutation(uploadReceipt, {
    onSuccess: () => onDone(),
    onError: (err: any) =>
      setError(err?.response?.data?.message || "Failed to upload receipt"),
  });

  const handleSubmit = () => {
    setError("");
    if (!doc?.key) return setError("Please upload the payment receipt");
    mutate({
      epid: payment.landOwnershipEpid,
      receiptDocumentKey: doc.key,
      receiptDocumentName: doc.name,
    });
  };

  const isRejected = payment?.status === "PAYMENT_REJECTED";

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 2, maxWidth: 500 }}
    >
      <Box sx={{ fontSize: 16, fontWeight: 600 }}>
        {isRejected ? "Re-upload Payment Receipt" : "Upload Payment Receipt"}
      </Box>
      {isRejected && payment?.rejectionRemark && (
        <Box sx={{ fontSize: 13, color: "#d32f2f" }}>
          <b>Rejection Reason:</b> {payment.rejectionRemark}
        </Box>
      )}
      <Box>
        <Box sx={{ fontSize: 12, color: "#666", mb: 0.5 }}>Payment Receipt</Box>
        <input
          type="file"
          accept="application/pdf,image/*"
          onChange={async (e) => {
            const file = e.target.files?.[0];
            if (!file) return;
            setError("");
            setUploading(true);
            try {
              setDoc(await uploadDocument(file));
            } catch {
              setError("File upload failed");
            } finally {
              setUploading(false);
            }
          }}
        />
        {uploading && <Box sx={{ fontSize: 12 }}>Uploading...</Box>}
        {doc && (
          <Box sx={{ fontSize: 12, color: "#2e7d32" }}>
            Uploaded: {doc.name}
          </Box>
        )}
      </Box>
      {error && <Box sx={{ color: "#d32f2f", fontSize: 13 }}>{error}</Box>}
      <Box>
        <Button text="Submit" loading={isLoading} onClick={handleSubmit} />
      </Box>
    </Box>
  );
};

const PaymentManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const epid = searchParams.get("epid") ?? "";

  const { data, isFetching, refetch } = useQuery(
    ["paymentByEpid", epid],
    () => getPaymentByEpid(epid),
    { enabled: !!epid }
  );

  const payment = data?.data?.payment;
  const mou = data?.data?.mou;
  const dpr = data?.data?.dpr;
  const queries = data?.data?.queries ?? [];
  const status = payment?.status;

  // Only entity users (userType === "ORGANIZATION") may upload/re-upload the
  // payment receipt. Other portal users (IO: userType === null, SNO:
  // userType === "INDIVIDUAL") get a read-only view.
  const userType = useAppSelector((state) => state.root.user.data.userType);
  const isEntity = userType === "ORGANIZATION";

  return (
    <DashboardWrapper
      title="Payment Management"
      showBackBtn
      backPath={path.payment}
    >
      <Box sx={{ p: 3, background: "white", borderRadius: 1 }}>
        {!epid ? (
          <Box sx={{ color: "#d32f2f" }}>No payment selected.</Box>
        ) : isFetching || !payment ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
                mb: 3,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ fontSize: 20, fontWeight: 600 }}>
                {payment.projectDetail?.name ?? "Project"}
              </Box>
              <Chip
                label={PAYMENT_STATUS_LABELS[status] ?? status}
                size="small"
                sx={{
                  backgroundColor: `${
                    PAYMENT_STATUS_COLORS[status] ?? "#757575"
                  }20`,
                  color: PAYMENT_STATUS_COLORS[status] ?? "#757575",
                  fontWeight: 600,
                }}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(3, 1fr)",
                gap: 2,
                mb: 4,
                pb: 3,
                borderBottom: "1px solid #eee",
              }}
            >
              <Field label="EPID">
                <Box
                  component="span"
                  onClick={() =>
                    navigate(
                      `${path.landOwnerShipDetailView}?${createSearchParams({
                        id: payment.landOwnershipEpid,
                      })}`
                    )
                  }
                  sx={greenLinkSx}
                >
                  {payment.landOwnershipEpid}
                </Box>
              </Field>
              <Field
                label="Registration No."
                value={payment.projectDetail?.registrationNo}
              />
              <Field label="DPR Version Id">
                {payment.dprVersionId ? (
                  <Box
                    component="span"
                    onClick={() =>
                      navigate(
                        `${path.dprForm}?${createSearchParams({
                          landOwnershipEpid: payment.landOwnershipEpid,
                        })}`
                      )
                    }
                    sx={greenLinkSx}
                  >
                    {payment.dprVersionId}
                  </Box>
                ) : (
                  "—"
                )}
              </Field>
              <Field
                label="DPR Approved Date"
                value={
                  dpr?.approvedAt
                    ? dateAndTimeStamp(dpr.approvedAt, "IST")
                    : "—"
                }
              />
              <Field
                label="MOU Approved Date"
                value={
                  mou?.approvedAt
                    ? dateAndTimeStamp(mou.approvedAt, "IST")
                    : "—"
                }
              />
              <Field label="MOU Document">
                <DocDownloadButton
                  docKey={mou?.documentKey}
                  docName={mou?.documentName}
                />
              </Field>
              <Field label="Demand Note (Signed)">
                <DocDownloadButton
                  docKey={payment.demandNoteDocumentKey}
                  docName={payment.demandNoteDocumentName}
                />
              </Field>
              <Field label="Payment Receipt">
                <DocDownloadButton
                  docKey={payment.receiptDocumentKey}
                  docName={payment.receiptDocumentName}
                />
              </Field>
            </Box>

            {isEntity &&
              (status === "PAYMENT_PENDING" ||
                status === "PAYMENT_REJECTED") && (
                <UploadReceiptForm payment={payment} onDone={() => refetch()} />
              )}
            {!isEntity &&
              (status === "PAYMENT_PENDING" ||
                status === "PAYMENT_REJECTED") && (
                <Box sx={{ color: "#666" }}>
                  {status === "PAYMENT_REJECTED"
                    ? "Payment receipt was rejected. Awaiting re-upload by the entity."
                    : "Awaiting payment receipt upload by the entity."}
                </Box>
              )}
            {status === "DEMAND_NOTE_PENDING" && (
              <Box sx={{ color: "#666" }}>
                Awaiting demand note from admin. You can begin payment once it
                is generated.
              </Box>
            )}
            {status === "PAYMENT_VERIFICATION" && (
              <Box sx={{ color: "#ed6c02" }}>
                Receipt uploaded. Awaiting admin verification.
              </Box>
            )}
            {status === "PAYMENT_RECEIVED" && (
              <Box sx={{ color: "#2e7d32", fontWeight: 600 }}>
                Payment received. Eco-restoration workflow can now begin.
              </Box>
            )}

            <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #eee" }}>
              <QueryThread queries={queries} />
              <RaiseQueryForm epid={epid} onDone={() => refetch()} />
            </Box>
          </>
        )}
      </Box>
    </DashboardWrapper>
  );
};

export default PaymentManagement;
